import {
	Box,
	type Component,
	Container,
	getCapabilities,
	Image,
	isImageLine,
	Spacer,
	Text,
	type TUI,
	truncateToWidth,
	visibleWidth,
} from "@kolisachint/hoocode-tui";
import type { ToolDefinition, ToolRenderContext } from "../../../core/extensions/types.js";
import { DEFAULT_TOOL_OUTPUT_VIEW, PEEK_LINES, type ToolOutputView } from "../../../core/tool-output-view.js";
import { createAllToolDefinitions, type ToolName } from "../../../core/tools/index.js";
import { getTextOutput as getRenderedTextOutput } from "../../../core/tools/render-utils.js";
import { convertToPng } from "../../../utils/image-convert.js";
import { theme } from "../theme/theme.js";
import { keyHint } from "./keybinding-hints.js";
import type { ChainEntry } from "./tool-chain-summary.js";
import { ToolSignalComponent, toolSubject } from "./tool-signal.js";

/**
 * Re-exported so tool-block callers keep a single import site for the view
 * dial; the type itself lives in core (the settings manager persists it).
 */
export type { ToolOutputView } from "../../../core/tool-output-view.js";

export interface ToolExecutionOptions {
	showImages?: boolean;
	imageWidthCells?: number;
	view?: ToolOutputView;
}

/**
 * Renders a child component and prepends a prefix (e.g. a status dot) to its
 * first line, indenting continuation lines so they align under the content.
 *
 * This keeps the dot inline with the first line instead of stacking it on its
 * own line (which happens when a Container holds the dot as a separate child).
 */
class PrefixFirstLine implements Component {
	private prefix: string;
	private child: Component;
	private indentWidth: number;
	// Keyed on the child's returned array identity so an unchanged child keeps
	// this wrapper reference-stable too (parents memoize flattening by ref).
	private memo?: { src: string[]; width: number; out: string[] };

	constructor(prefix: string, child: Component) {
		this.prefix = prefix;
		this.child = child;
		this.indentWidth = visibleWidth(prefix);
	}

	invalidate(): void {
		this.child.invalidate?.();
		this.memo = undefined;
	}

	render(width: number): string[] {
		// Reserve room for the prefix so the child wraps within the remaining width.
		const childWidth = Math.max(1, width - this.indentWidth);
		const lines = this.child.render(childWidth);
		if (this.memo && this.memo.src === lines && this.memo.width === width) {
			return this.memo.out;
		}
		const indent = " ".repeat(this.indentWidth);
		const out =
			lines.length === 0 ? [this.prefix] : lines.map((line, i) => (i === 0 ? this.prefix + line : indent + line));
		this.memo = { src: lines, width, out };
		return out;
	}
}

/**
 * Indents every line of a child, unlike PrefixFirstLine which only offsets the
 * first. Used for a failure's body under a radar row, so the reason reads as
 * hanging off the row rather than starting a new column.
 */
class IndentAll implements Component {
	private child: Component;
	private indent: string;
	private memo?: { src: string[]; width: number; out: string[] };

	constructor(child: Component, width: number) {
		this.child = child;
		this.indent = " ".repeat(width);
	}

	invalidate(): void {
		this.child.invalidate?.();
		this.memo = undefined;
	}

	render(width: number): string[] {
		const lines = this.child.render(Math.max(1, width - this.indent.length));
		if (this.memo && this.memo.src === lines && this.memo.width === width) return this.memo.out;
		const out = lines.map((line) => (line.trim() === "" ? line : this.indent + line));
		this.memo = { src: lines, width, out };
		return out;
	}
}

export class ToolExecutionComponent extends Container {
	private contentBox: Box;
	private contentText: Text;
	private selfRenderContainer: Container;
	private callRendererComponent?: Component;
	private resultRendererComponent?: Component;
	private rendererState: any = {};
	private imageComponents: Image[] = [];
	private imageSpacers: Spacer[] = [];
	private toolName: string;
	private toolCallId: string;
	private args: any;
	// Persisted view (radar/peek/full). The block's only display state: it decides
	// whether the call line is the uniform radar row or the tool's own renderer,
	// whether the result body renders at all, and whether that body is trimmed.
	// There is no per-block override — `app.tools.expand` moves the dial itself.
	private view: ToolOutputView;
	private signalComponent?: ToolSignalComponent;
	private leadingSpacer: Spacer;
	private showImages: boolean;
	private imageWidthCells: number;
	private isPartial = true;
	private toolDefinition?: ToolDefinition<any, any>;
	private builtInToolDefinition?: ToolDefinition<any, any>;
	private ui: TUI;
	private cwd: string;
	private executionStarted = false;
	private argsComplete = false;
	private result?: {
		content: Array<{ type: string; text?: string; data?: string; mimeType?: string }>;
		isError: boolean;
		details?: any;
	};
	private convertedImages: Map<number, { data: string; mimeType: string }> = new Map();
	private hideComponent = false;
	// Once a finished tool block has scrolled far out of view the interactive mode
	// freezes it (see freeze()): its already-rendered lines are captured and the
	// heavy source payloads (full tool result, base64 image copies, per-renderer
	// state, child component caches) are released. A frozen block is immutable and
	// no longer reflows on resize — old off-screen scrollback keeps its wrapping,
	// which self-heals on any full rebuild (theme toggle / session reload, both of
	// which reconstruct components from the intact session data).
	private frozen = false;
	private frozenLines?: string[];
	/** Width the frozen snapshot was captured at; a wider terminal is safe as-is,
	 * a narrower one needs re-truncation to avoid the TUI's over-width crash guard. */
	private frozenWidth = 0;
	private frozenTruncated?: string[];
	private frozenTruncatedWidth = -1;

	constructor(
		toolName: string,
		toolCallId: string,
		args: any,
		options: ToolExecutionOptions = {},
		toolDefinition: ToolDefinition<any, any> | undefined,
		ui: TUI,
		cwd: string,
	) {
		super();
		this.toolName = toolName;
		this.toolCallId = toolCallId;
		this.args = args;
		this.toolDefinition = toolDefinition;
		this.builtInToolDefinition = createAllToolDefinitions(cwd)[toolName as ToolName];
		this.showImages = options.showImages ?? true;
		this.imageWidthCells = options.imageWidthCells ?? 60;
		this.view = options.view ?? DEFAULT_TOOL_OUTPUT_VIEW;
		this.ui = ui;
		this.cwd = cwd;

		// The separator between consecutive tool blocks. Radar drops it: rows that
		// are one line each only read as a map when they stack without gaps.
		this.leadingSpacer = new Spacer(1);
		this.addChild(this.leadingSpacer);

		// Always create all shell variants. contentBox is used for default renderer-based composition.
		// selfRenderContainer is used when the tool renders its own framing.
		// contentText is reserved for generic fallback rendering when no tool definition exists.
		// Boxless: no filled background — hierarchy comes from status dots + indent.
		// paddingY is 0: the single leading Spacer(1) is the only separator between
		// tool blocks, so consecutive commands don't stack 3 blank lines between them.
		this.contentBox = new Box(1, 0);
		this.contentText = new Text("", 1, 0);
		this.selfRenderContainer = new Container();

		if (this.hasRendererDefinition()) {
			this.addChild(this.activeShell());
		} else {
			this.addChild(this.contentText);
		}

		this.updateDisplay();
	}

	private getCallRenderer(): ToolDefinition<any, any>["renderCall"] | undefined {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderCall;
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderCall;
		}
		return this.toolDefinition.renderCall ?? this.builtInToolDefinition.renderCall;
	}

	private getResultRenderer(): ToolDefinition<any, any>["renderResult"] | undefined {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderResult;
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderResult;
		}
		return this.toolDefinition.renderResult ?? this.builtInToolDefinition.renderResult;
	}

	private hasRendererDefinition(): boolean {
		return this.builtInToolDefinition !== undefined || this.toolDefinition !== undefined;
	}

	private getRenderShell(): "default" | "self" {
		if (!this.builtInToolDefinition) {
			return this.toolDefinition?.renderShell ?? "default";
		}
		if (!this.toolDefinition) {
			return this.builtInToolDefinition.renderShell ?? "default";
		}
		return this.toolDefinition.renderShell ?? this.builtInToolDefinition.renderShell ?? "default";
	}

	private getRenderContext(lastComponent: Component | undefined): ToolRenderContext {
		return {
			args: this.args,
			toolCallId: this.toolCallId,
			invalidate: () => {
				this.invalidate();
				this.ui.requestRender();
			},
			lastComponent,
			state: this.rendererState,
			cwd: this.cwd,
			executionStarted: this.executionStarted,
			argsComplete: this.argsComplete,
			isPartial: this.isPartial,
			expanded: this.view === "full",
			showImages: this.showImages,
			isError: this.result?.isError ?? false,
		};
	}

	private createCallFallback(): Component {
		return new Text(theme.fg("toolTitle", theme.bold(this.toolName)), 0, 0);
	}

	/**
	 * The body for a tool with no result renderer of its own.
	 *
	 * It gets the same peek budget as every renderer that trims itself. Before
	 * the dial had a trimmed stop this path could print its whole result and
	 * nothing looked wrong; now an untrimmed extension tool would be the one
	 * thing on screen that ignores where the dial is set.
	 */
	private createResultFallback(): Component | undefined {
		const output = this.getTextOutput();
		if (!output) {
			return undefined;
		}
		if (this.view === "full") return new Text(theme.fg("toolOutput", output), 0, 0);
		const lines = output.split("\n");
		if (lines.length <= PEEK_LINES) return new Text(theme.fg("toolOutput", output), 0, 0);
		const shown = lines.slice(0, PEEK_LINES).join("\n");
		const remaining = lines.length - PEEK_LINES;
		return new Text(
			theme.fg("toolOutput", shown) +
				theme.fg("muted", `\n... (${remaining} more lines, `) +
				keyHint("app.tools.expand", "to expand") +
				theme.fg("muted", ")"),
			0,
			0,
		);
	}

	updateArgs(args: any): void {
		this.args = args;
		this.updateDisplay();
	}

	markExecutionStarted(): void {
		this.executionStarted = true;
		this.updateDisplay();
		this.ui.requestRender();
	}

	setArgsComplete(): void {
		this.argsComplete = true;
		this.updateDisplay();
		this.ui.requestRender();
	}

	updateResult(
		result: {
			content: Array<{ type: string; text?: string; data?: string; mimeType?: string }>;
			details?: any;
			isError: boolean;
		},
		isPartial = false,
	): void {
		this.result = result;
		this.isPartial = isPartial;
		this.updateDisplay();
		this.maybeConvertImagesForKitty();
	}

	private maybeConvertImagesForKitty(): void {
		const caps = getCapabilities();
		if (caps.images !== "kitty") return;
		if (!this.result) return;

		const imageBlocks = this.result.content.filter((c) => c.type === "image");
		for (let i = 0; i < imageBlocks.length; i++) {
			const img = imageBlocks[i];
			if (!img.data || !img.mimeType) continue;
			if (img.mimeType === "image/png") continue;
			if (this.convertedImages.has(i)) continue;

			const index = i;
			convertToPng(img.data, img.mimeType).then((converted) => {
				if (this.frozen) return;
				if (converted) {
					this.convertedImages.set(index, converted);
					this.updateDisplay();
					this.ui.requestRender();
				}
			});
		}
	}

	setView(view: ToolOutputView): void {
		this.view = view;
		this.updateDisplay();
	}

	/** This call's contribution to its chain's summary line. */
	chainEntry(): ChainEntry {
		const output = this.result ? this.getTextOutput() : "";
		return {
			tool: this.toolName,
			subject: toolSubject(this.args, this.cwd),
			isError: this.result?.isError === true,
			isPartial: this.result === undefined || this.isPartial,
			outputLines: output.trim() ? output.split("\n").length : 0,
		};
	}

	/** The failure text, for a collapsed chain to print under its line. */
	errorText(): string {
		return this.result?.isError ? this.getTextOutput() : "";
	}

	/**
	 * Whether the result body should render.
	 *
	 * A failure always shows its reason, in every view. Everything else about
	 * these views is a judgement about how much detail you want; why something
	 * broke is not detail, and a red dot with no explanation was the one thing
	 * the folded views got wrong.
	 */
	private shouldShowBody(): boolean {
		return this.view !== "radar" || this.result?.isError === true;
	}

	/**
	 * Whether this block draws its radar signal line instead of the tool's own
	 * call renderer.
	 */
	private isLatest = false;

	private shouldShowSignalLine(): boolean {
		return this.view === "radar";
	}

	/**
	 * Whether this block draws its own blank row above itself.
	 *
	 * Radar rows do not — rows that are one line each only read as a map when
	 * they stack without gaps — which leaves the first row of a run with nothing
	 * between it and whatever introduced it. `ToolChainComponent` asks this so
	 * the run can draw that one gap itself without doubling a gap the block was
	 * already drawing, which is what happens in every other view and for a radar
	 * block that has been opened.
	 */
	drawsLeadingGap(): boolean {
		return !this.shouldShowSignalLine();
	}

	/**
	 * A failure's body under a radar row hangs off the row; anywhere else it sits
	 * directly under its own call line and needs no extra indent.
	 */
	private indentUnderSignalRow(component: Component): Component {
		return this.shouldShowSignalLine() ? new IndentAll(component, 3) : component;
	}

	/**
	 * Mark this block as the newest call in the transcript, or no longer it.
	 *
	 * Only radar draws anything from it: the other two views already show each
	 * call in full, so there is nothing for a "where am I" mark to add.
	 */
	setLatest(isLatest: boolean): void {
		if (this.isLatest === isLatest) return;
		this.isLatest = isLatest;
		this.signalComponent?.invalidate();
		this.updateDisplay();
	}

	private getSignalComponent(): ToolSignalComponent {
		const input = {
			toolName: this.toolName,
			args: this.args,
			cwd: this.cwd,
			result: this.result,
			isPartial: this.isPartial,
			showImages: this.showImages,
			isLatest: this.isLatest,
		};
		if (this.signalComponent) {
			this.signalComponent.setInput(input);
		} else {
			this.signalComponent = new ToolSignalComponent(input);
		}
		return this.signalComponent;
	}

	setShowImages(show: boolean): void {
		this.showImages = show;
		this.updateDisplay();
	}

	setImageWidthCells(width: number): void {
		this.imageWidthCells = Math.max(1, Math.floor(width));
		this.updateDisplay();
	}

	override invalidate(): void {
		// A frozen block has released the state updateDisplay would rebuild from;
		// its captured snapshot is authoritative, so skip the rebuild entirely.
		if (this.frozen) return;
		super.invalidate();
		this.updateDisplay();
	}

	/**
	 * True when this block is a finished, visible tool result that still holds its
	 * heavy source payloads — i.e. a candidate for freeze() once it scrolls out of
	 * the live window. In-flight (partial) and already-frozen blocks are excluded.
	 */
	isFreezable(): boolean {
		return !this.frozen && !this.isPartial && !this.hideComponent && !!this.result;
	}

	/** Mark this block for freezing; the snapshot is captured on the next render
	 * (at the real render width) and the heavy state released then. */
	freeze(): void {
		if (this.isFreezable()) this.frozen = true;
	}

	private releaseHeavyState(): void {
		this.result = undefined;
		this.convertedImages.clear();
		this.imageComponents = [];
		this.imageSpacers = [];
		this.rendererState = {};
		this.callRendererComponent = undefined;
		this.resultRendererComponent = undefined;
		// Drop child components so their line caches (which embed base64 image
		// payloads and full text output) become collectable.
		this.clear();
	}

	override render(width: number): string[] {
		if (this.hideComponent) {
			return [];
		}
		if (this.frozenLines) {
			// Captured snapshot. Wider (or equal) terminal: emit as-is. Narrower:
			// re-truncate non-image lines to avoid the TUI's over-width crash guard,
			// caching the result per width so it stays O(1) on unchanged frames.
			if (width >= this.frozenWidth) return this.frozenLines;
			if (this.frozenTruncatedWidth === width && this.frozenTruncated) return this.frozenTruncated;
			this.frozenTruncated = this.frozenLines.map((line) =>
				isImageLine(line) || visibleWidth(line) <= width ? line : truncateToWidth(line, width),
			);
			this.frozenTruncatedWidth = width;
			return this.frozenTruncated;
		}
		const lines = super.render(width);
		if (this.frozen) {
			this.frozenLines = lines;
			this.frozenWidth = width;
			this.releaseHeavyState();
		}
		return lines;
	}

	/**
	 * The container this block renders into.
	 *
	 * A self-rendering tool (`edit` draws its own framed diff) gets the bare
	 * container so its frame can use the full width; everything else gets the
	 * padded box. The bare container costs a column of alignment, though — its
	 * status dot sits one left of every other row — so only the stop that exists
	 * for reading pays it. Below `full` every block takes the padded box, which
	 * is what lets radar's rows and peek's call lines line up into a column.
	 */
	private activeShell(): Box | Container {
		if (this.view !== "full") return this.contentBox;
		return this.getRenderShell() === "self" ? this.selfRenderContainer : this.contentBox;
	}

	/** Swap the attached shell when the view changes which one applies. */
	private syncShellChild(): void {
		const wanted = this.activeShell();
		const other = wanted === this.contentBox ? this.selfRenderContainer : this.contentBox;
		if (this.children.includes(wanted)) return;
		this.removeChild(other);
		other.clear();
		this.addChild(wanted);
	}

	private updateDisplay(): void {
		// Frozen blocks are immutable snapshots with their source state released.
		if (this.frozen) return;
		let hasContent = false;
		this.hideComponent = false;
		this.leadingSpacer.setLines(this.shouldShowSignalLine() ? 0 : 1);
		if (this.hasRendererDefinition()) {
			this.syncShellChild();
			const renderContainer = this.activeShell();
			// Boxless: no background fill on the container.
			if (renderContainer instanceof Box) {
				renderContainer.setBgFn(undefined);
			}
			renderContainer.clear();

			// Status dot prefix: green for complete success, yellow for pending/partial, red for error.
			// The dot is prepended to the first line of the call renderer so it stays inline
			// (adding it as a separate child would stack it on its own line).
			const dotColor = this.result?.isError ? "error" : this.isPartial ? "warning" : "success";
			// No disclosure caret. It is a click-target idiom in a TUI with no
			// pointer, it cost two columns on every row for an action that could
			// only ever be reached by keyboard, and reading it as "this row opens"
			// set an expectation the view does not meet.
			const dot = theme.fg(dotColor, "● ");

			// Radar replaces the tool's own call renderer with the uniform signal
			// row, so a screen of mixed tools lines up into one readable map.
			if (this.shouldShowSignalLine()) {
				renderContainer.addChild(new PrefixFirstLine(dot, this.getSignalComponent()));
				hasContent = true;
			} else {
				const callRenderer = this.getCallRenderer();
				if (!callRenderer) {
					renderContainer.addChild(new PrefixFirstLine(dot, this.createCallFallback()));
					hasContent = true;
				} else {
					try {
						const component = callRenderer(this.args, theme, this.getRenderContext(this.callRendererComponent));
						this.callRendererComponent = component;
						renderContainer.addChild(new PrefixFirstLine(dot, component));
						hasContent = true;
					} catch {
						this.callRendererComponent = undefined;
						renderContainer.addChild(new PrefixFirstLine(dot, this.createCallFallback()));
						hasContent = true;
					}
				}
			}

			if (this.result && this.shouldShowBody()) {
				const resultRenderer = this.getResultRenderer();
				if (!resultRenderer) {
					const component = this.createResultFallback();
					if (component) {
						renderContainer.addChild(this.indentUnderSignalRow(component));
						hasContent = true;
					}
				} else {
					try {
						const component = resultRenderer(
							{ content: this.result.content as any, details: this.result.details },
							{ expanded: this.view === "full", isPartial: this.isPartial },
							theme,
							this.getRenderContext(this.resultRendererComponent),
						);
						this.resultRendererComponent = component;
						renderContainer.addChild(this.indentUnderSignalRow(component));
						hasContent = true;
					} catch {
						this.resultRendererComponent = undefined;
						const component = this.createResultFallback();
						if (component) {
							renderContainer.addChild(component);
							hasContent = true;
						}
					}
				}
			}
		} else {
			// Boxless: no background fill.
			this.contentText.setCustomBgFn(undefined);
			this.contentText.setText(this.formatToolExecution());
			hasContent = true;
		}

		for (const img of this.imageComponents) {
			this.removeChild(img);
		}
		this.imageComponents = [];
		for (const spacer of this.imageSpacers) {
			this.removeChild(spacer);
		}
		this.imageSpacers = [];

		if (this.result && !this.shouldShowSignalLine()) {
			const imageBlocks = this.result.content.filter((c) => c.type === "image");
			const caps = getCapabilities();
			for (let i = 0; i < imageBlocks.length; i++) {
				const img = imageBlocks[i];
				if (caps.images && this.showImages && img.data && img.mimeType) {
					const converted = this.convertedImages.get(i);
					const imageData = converted?.data ?? img.data;
					const imageMimeType = converted?.mimeType ?? img.mimeType;
					if (caps.images === "kitty" && imageMimeType !== "image/png") continue;

					const spacer = new Spacer(1);
					this.addChild(spacer);
					this.imageSpacers.push(spacer);
					const imageComponent = new Image(
						imageData,
						imageMimeType,
						{ fallbackColor: (s: string) => theme.fg("toolOutput", s) },
						{ maxWidthCells: this.imageWidthCells },
					);
					this.imageComponents.push(imageComponent);
					this.addChild(imageComponent);
				}
			}
		}

		if (this.hasRendererDefinition() && !hasContent && this.imageComponents.length === 0) {
			this.hideComponent = true;
		}
	}

	private getTextOutput(): string {
		return getRenderedTextOutput(this.result, this.showImages);
	}

	private formatToolExecution(): string {
		const dotColor = this.result?.isError ? "error" : this.isPartial ? "warning" : "success";
		// A tool with no renderer still gets a radar row, just without the
		// flush-right signal column (this path has no width to align against).
		if (this.shouldShowSignalLine()) {
			const subject = toolSubject(this.args, this.cwd);
			return (
				theme.fg(dotColor, "● ") +
				theme.fg("toolTitle", theme.bold(this.toolName)) +
				(subject ? ` ${theme.fg("toolOutput", subject)}` : "")
			);
		}
		let text = theme.fg(dotColor, "● ") + theme.fg("toolTitle", theme.bold(this.toolName));
		const content = JSON.stringify(this.args, null, 2);
		if (content) {
			text += `\n\n${content}`;
		}
		const output = this.shouldShowBody() ? this.getTextOutput() : "";
		if (output) {
			text += `\n${output}`;
		}
		return text;
	}
}
